import React, { useEffect, useState } from 'react';
import styles from './ListBlock.module.scss';
import { Container } from '@src/elements/Grid';
import { ListBlockFragment } from '@src/graphql/gql-types';
import Heading from '@src/elements/Heading';
import RichTextRender from '../RichTextRender';
import { ContentfulPageTypes } from '@src/types/contentful';
import { RICHTEXT_RENDER_MODE } from '@src/types/richText';
import { ButtonLinkStandard } from '../ButtonLink';
import { getLink } from '@src/utils/url';
import BoxIcon from '@src/elements/BoxIcon';
import {
  COLOR_OUTERSPACE,
  ContentBlockBackgroundColors
} from '@src/types/colors';
import MediaImage from '../MediaImage';
import classNames from 'classnames';
import { getColorClasses } from '@src/utils/colorClasses';
import { useViewport } from '@src/hooks/useViewport';
import { MD_BREAKPOINT } from '@src/constants/breakpoints';

type ListBlock = ListBlockFragment & {
  pageType: ContentfulPageTypes;
  backgroundColor?: ContentBlockBackgroundColors;
};

const ListBlock: React.FC<ListBlock> = ({
  heading,
  bodyContent,
  primaryCta,
  mediaImage,
  listItemsCollection,
  pageType,
  backgroundColor
}) => {
  const { width } = useViewport();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(!!width && width < MD_BREAKPOINT);
  }, [width]);

  return (
    <div
      className={classNames(
        styles.listBlock,
        backgroundColor ? getColorClasses(backgroundColor, styles) : ''
      )}
    >
      <Container containerWidth="fluid" className={styles.container}>
        <div className={styles.leftContent}>
          <div className={styles.leftContentHeading}>
            {heading && (
              <Heading priority={2} fontStyle="h1">
                {heading}
              </Heading>
            )}

            {bodyContent && (
              <RichTextRender
                bodyContent={bodyContent}
                pageType={pageType}
                renderMode={RICHTEXT_RENDER_MODE.NO_BLOCK_EMBEDS}
              />
            )}
          </div>

          {primaryCta && !isMobile && (
            <ButtonLinkStandard
              href={getLink(
                primaryCta.internalLink,
                primaryCta.urlLink,
                primaryCta.assetLink
              )}
              color={COLOR_OUTERSPACE}
              size="md"
            >
              {primaryCta.label}
              <BoxIcon name="right-arrow-alt" size="sm" />
            </ButtonLinkStandard>
          )}

          {mediaImage && (
            <div className={styles.mediaImage}>
              <MediaImage {...mediaImage} />
            </div>
          )}
        </div>

        <div className={styles.rightContent}>
          {listItemsCollection?.items.map((item) => (
            <div key={item?.sys.id} className={styles.listItem}>
              <div className={styles.listItemIcon}>
                <BoxIcon name="check" size="md" color={COLOR_OUTERSPACE} />
              </div>
              <div className={styles.listItemContent}>
                <p>{item?.heading}</p>
                <p>{item?.description}</p>
              </div>
            </div>
          ))}
        </div>

        {primaryCta && isMobile && (
          <ButtonLinkStandard
            href={getLink(
              primaryCta.internalLink,
              primaryCta.urlLink,
              primaryCta.assetLink
            )}
            color={COLOR_OUTERSPACE}
            size="md"
          >
            {primaryCta.label}
            <BoxIcon name="right-arrow-alt" size="sm" />
          </ButtonLinkStandard>
        )}
      </Container>
    </div>
  );
};

export default ListBlock;
